import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import X from 'lucide-react/dist/esm/icons/x';
import Upload from 'lucide-react/dist/esm/icons/upload';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import Target from 'lucide-react/dist/esm/icons/target';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import FilePlus from 'lucide-react/dist/esm/icons/file-plus';
import { Spinner } from '../../../components/ui';
import { getTranslation } from '../../../i18n';
import { getAnalysisCompleteness } from '../../../services/api';

/**
 * Circular Progress Ring Component
 */
const ProgressRing = ({ score, size = 100, strokeWidth = 8, label = 'Complete' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 80) return { stroke: '#10b981', glow: 'rgba(16, 185, 129, 0.4)' };
    if (score >= 60) return { stroke: '#f59e0b', glow: 'rgba(245, 158, 11, 0.4)' };
    return { stroke: '#ef4444', glow: 'rgba(239, 68, 68, 0.4)' };
  };
  
  const color = getColor(score);
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color.stroke}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 1s ease-out',
            filter: `drop-shadow(0 0 12px ${color.glow})`
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-content">{score}%</span>
        <span className="text-[10px] text-content-tertiary uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
};

/**
 * Missing Document Card
 */
const MissingDocCard = ({ doc, language, onSelect, selected }) => {
  const getLocalizedText = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[language] || obj.en || '';
  };
  
  const docType = doc.document_type || 'Document';
  const reason = getLocalizedText(doc.reason) || getLocalizedText(doc.what_it_enables) || '';
  const guidance = getLocalizedText(doc.suggested_guidance) || getLocalizedText(doc.specific_request) || '';
  
  return (
    <div 
      className={`group p-3 rounded-lg border transition-all cursor-pointer ${
        selected 
          ? 'bg-violet-500/20 border-violet-500/50' 
          : 'bg-surface-primary border-subtle hover:border-violet-500/30'
      }`}
      onClick={() => onSelect(guidance)}
    >
      <div className="flex items-start gap-3">
        <div className="p-1.5 rounded-md bg-orange-500/20 text-orange-400 shrink-0 mt-0.5">
          <FileText size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-content capitalize block">
            {docType.replace(/_/g, ' ')}
          </span>
          {reason && (
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">{reason}</p>
          )}
        </div>
        {selected && <CheckCircle size={14} className="text-violet-400 shrink-0" />}
      </div>
    </div>
  );
};

/**
 * Improvement Suggestion Card
 */
const SuggestionCard = ({ suggestion, language, onSelect, selected }) => {
  const getLocalizedText = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[language] || obj.en || '';
  };
  
  const text = getLocalizedText(suggestion.suggestion) || '';
  const scoreIncrease = suggestion.score_increase || suggestion.estimated_score_increase;
  
  return (
    <div 
      className={`group p-3 rounded-lg border transition-all cursor-pointer ${
        selected 
          ? 'bg-violet-500/20 border-violet-500/50' 
          : 'bg-gradient-to-r from-violet-500/5 to-cyan-500/5 border-violet-500/20 hover:border-violet-500/40'
      }`}
      onClick={() => onSelect(text)}
    >
      <div className="flex items-start gap-3">
        <div className="p-1.5 bg-violet-500/20 rounded-md shrink-0 mt-0.5">
          <TrendingUp size={14} className="text-violet-400" />
        </div>
        <span className="text-sm text-content flex-1 leading-relaxed">{text}</span>
        <div className="flex items-center gap-2 shrink-0">
          {scoreIncrease && (
            <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full font-medium">
              +{scoreIncrease}%
            </span>
          )}
          {selected && <CheckCircle size={14} className="text-violet-400" />}
        </div>
      </div>
    </div>
  );
};

/**
 * Improve Analysis Modal
 * Fetches completeness assessment from Claude API when opened
 */
const ImproveAnalysisModal = ({ 
  isOpen, 
  onClose, 
  language = 'en',
  onExtendAnalysis,
  analysisId,
  isExtending = false
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [guidance, setGuidance] = useState('');
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [completenessData, setCompletenessData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const fileInputRef = useRef(null);
  const t = (key, variables = {}) => getTranslation(language, key, variables);
  
  // Fetch completeness data from API when modal opens
  useEffect(() => {
    if (isOpen && analysisId && !completenessData) {
      setIsLoading(true);
      setLoadError(null);
      
      getAnalysisCompleteness(analysisId)
        .then(data => {
          setCompletenessData(data);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load completeness:', err);
          setLoadError('Failed to analyze. Please try again.');
          setIsLoading(false);
        });
    }
  }, [isOpen, analysisId, completenessData]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setGuidance('');
      setSelectedSuggestions([]);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  // Extract data from completeness response
  const {
    overall_score = 0,
    score_explanation = '',
    statements_found = {},
    missing_documents = [],
    improvement_suggestions = [],
    metrics_available = {},
    metrics_missing = []
  } = completenessData || {};
  
  // Calculate statements found count
  const statementsEntries = Object.entries(statements_found);
  const statementsFoundCount = statementsEntries.filter(([, v]) => (v as any)?.present).length;
  const totalStatements = statementsEntries.length || 4;
  
  // Calculate metrics count
  const allMetrics = [
    ...(metrics_available.profitability || []),
    ...(metrics_available.liquidity || []),
    ...(metrics_available.efficiency || []),
    ...(metrics_available.leverage || [])
  ];
  const metricsAvailableCount = allMetrics.length;
  const metricsMissingCount = metrics_missing?.length || 0;
  const totalMetricsCount = Math.max(metricsAvailableCount + metricsMissingCount, 8);
  
  // Multi-select handler: toggle suggestion in array and update guidance
  const handleSuggestionSelect = (suggestionText) => {
    setSelectedSuggestions(prev => {
      const isSelected = prev.includes(suggestionText);
      const newSelected = isSelected
        ? prev.filter(s => s !== suggestionText)
        : [...prev, suggestionText];
      
      // Update guidance with combined selected suggestions
      setGuidance(newSelected.join('\n• '));
      return newSelected;
    });
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };
  
  const handleSubmit = () => {
    if (selectedFiles.length > 0 && onExtendAnalysis) {
      onExtendAnalysis(analysisId, selectedFiles, guidance);
    }
  };
  
  const getScoreLabel = (score) => {
    if (score >= 90) return { text: t('completenessExcellent') || 'Excellent', color: 'text-emerald-400' };
    if (score >= 75) return { text: t('completenessGood') || 'Good', color: 'text-green-400' };
    if (score >= 60) return { text: t('completenessModerate') || 'Moderate', color: 'text-yellow-400' };
    return { text: t('completenessNeedsWork') || 'Needs Improvement', color: 'text-orange-400' };
  };
  
  const scoreLabel = getScoreLabel(overall_score);
  
  return createPortal(
    <>
      {/* Backdrop - separate layer to cover everything */}
      <div 
        className="fixed inset-0 z-overlay bg-black/90"
        onClick={onClose}
      />
      
      {/* Modal Container - highest z-index */}
      <div className="fixed inset-0 z-modal flex items-center justify-center p-8 pointer-events-none">
        {/* Modal - centered in viewport */}
        <div className="pointer-events-auto w-full max-w-4xl max-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 to-slate-800 border border-strong rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-default">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-violet-500/20 rounded-lg">
              <Sparkles size={18} className="text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-content">
                {t('improveAnalysis') || 'Improve Your Analysis'}
              </h2>
              <p className="text-xs text-slate-400">
                {t('improveAnalysisSubtitle') || 'Upload additional documents to get a more complete analysis'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors"
          >
            <X size={18} className="text-slate-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-5">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner size="lg" className="text-violet-400 mb-4" />
              <p className="text-content font-medium">{t('analyzingDocuments') || 'Analyzing your documents...'}</p>
              <p className="text-xs text-slate-400 mt-1">{t('claudeAssessing') || 'Claude is assessing what\'s missing'}</p>
            </div>
          )}
          
          {/* Error State */}
          {loadError && !isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={40} className="text-status-error mb-4" />
              <p className="text-content font-medium">{loadError}</p>
              <button 
                onClick={() => { setCompletenessData(null); setLoadError(null); }}
                className="mt-3 px-4 py-2 text-sm bg-violet-600 hover:bg-violet-500 rounded-lg text-content transition-colors"
              >
                {t('tryAgain') || 'Try Again'}
              </button>
            </div>
          )}
          
          {/* Score Overview - Only show when loaded */}
          {!isLoading && !loadError && (
            <>
              <div className="flex items-center gap-5 mb-4 p-4 bg-surface-secondary rounded-xl border border-subtle">
                <ProgressRing score={overall_score} label={t('complete') || 'Complete'} />
                
                <div className="flex-1 space-y-2">
                  <div>
                    <span className={`text-base font-bold ${scoreLabel.color}`}>
                      {scoreLabel.text}
                    </span>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {score_explanation || (overall_score >= 80 
                        ? t('analysisComprehensive') || 'Your analysis is quite comprehensive!' 
                        : t('addMoreDocsForInsights') || 'Add more documents to unlock deeper insights.')}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-slate-500" />
                      <span className="text-xs font-medium text-content">
                        {statementsFoundCount}/{totalStatements}
                      </span>
                      <span className="text-[10px] text-slate-500">{t('statements') || 'Statements'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target size={14} className="text-slate-500" />
                      <span className="text-xs font-medium text-content">
                        {metricsAvailableCount}/{totalMetricsCount}
                      </span>
                      <span className="text-[10px] text-slate-500">{t('metrics') || 'Metrics'}</span>
                    </div>
                  </div>
                </div>
              </div>
          
          {/* Suggestions Section */}
          {(missing_documents.length > 0 || improvement_suggestions.length > 0) && (
            <div className="mb-4">
              <h3 className="flex items-center gap-2 text-xs font-semibold text-content mb-2">
                <AlertCircle size={14} className="text-orange-400" />
                {t('recommendedUploads') || 'Recommended Documents to Upload'}
              </h3>
              <div className="grid gap-3">
                {missing_documents.map((doc, idx) => (
                  <MissingDocCard 
                    key={`doc-${idx}`} 
                    doc={doc} 
                    language={language}
                    onSelect={handleSuggestionSelect}
                    selected={selectedSuggestions.includes(doc.suggested_guidance || doc.specific_request)}
                  />
                ))}
                {improvement_suggestions.map((suggestion, idx) => (
                  <SuggestionCard 
                    key={`sug-${idx}`} 
                    suggestion={suggestion} 
                    language={language}
                    onSelect={handleSuggestionSelect}
                    selected={selectedSuggestions.includes(suggestion.suggestion)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Upload Section */}
          <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-xs font-semibold text-content">
              <FilePlus size={14} className="text-violet-400" />
              {t('uploadNewDocuments') || 'Upload New Documents'}
            </h3>
            
            {/* File Drop Zone */}
            <div 
              className="relative border-2 border-dashed border-strong rounded-lg py-4 px-6 text-center hover:border-violet-500/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <Upload size={24} className="mx-auto text-slate-500 mb-2" />
              <p className="text-sm text-content font-medium">
                {selectedFiles.length > 0 
                  ? t('filesSelectedCount', { count: selectedFiles.length }) || `${selectedFiles.length} file(s) selected`
                  : t('clickToUploadPdf') || 'Click to upload PDF files'}
              </p>
              <p className="text-[10px] text-slate-500">
                {t('dropFinancialDocs') || 'Drop your financial documents here'}
              </p>
              
              {selectedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-center gap-2 text-xs text-violet-400">
                      <FileText size={12} />
                      <span className="truncate max-w-[200px]">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Guidance Input */}
            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">
                {t('additionalGuidance') || 'Additional Guidance'} 
                <span className="text-slate-500 ml-1">({t('optional') || 'optional'})</span>
              </label>
              <textarea
                value={guidance}
                onChange={(e) => setGuidance(e.target.value)}
                placeholder={t('guidancePlaceholder') || "e.g., Focus on liquidity analysis, compare with last year's results..."}
                className="w-full h-16 px-3 py-2 bg-surface-secondary border border-default rounded-lg text-content text-xs placeholder-slate-500 focus:ring-2 focus:ring-violet-500/50 focus:border-transparent resize-none"
              />
            </div>
          </div>
          </>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-4 border-t border-default bg-slate-900/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-content transition-colors"
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0 || isExtending}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed rounded-lg text-content-inverse font-semibold text-xs transition-all shadow-lg shadow-violet-500/20 hover:shadow-violet-500/40"
          >
            {isExtending ? (
              <>
                <Spinner size="sm" />
                {t('extending') || 'Extending Analysis...'}
              </>
            ) : (
              <>
                <Upload size={14} />
                {t('extendAnalysis') || 'Extend Analysis'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>,
    document.body
  );
};

export default ImproveAnalysisModal;
